import Tabs from '../../../components/AnimatedTabs';
import Table from "../../../components/RecordTable";
import React, { useState, useEffect } from "react";
import {
  Card, Button, Input, Tag, Typography, Space, Checkbox,
  message,  Divider, Row, Col,
} from "antd";
import { SaveOutlined, ReloadOutlined, FileProtectOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { policyAPI } from "../../../services/api";

const { Title, Text } = Typography;
const { TextArea } = Input;

const Policy = () => {
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("Terms & Conditions / Privacy Policy");
  const [version, setVersion] = useState("1.0");
  const [audiences, setAudiences] = useState(['patient', 'doctor']);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [acceptances, setAcceptances] = useState([]);
  const [accLoading, setAccLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchPolicy = async () => {
    setLoading(true);
    try {
      const res = await policyAPI.get();
      const p = res?.data;
      if (p) {
        setAudiences(p.audiences || ['patient', 'doctor']);
        setContent(p.content || "");
        setTitle(p.title || "Terms & Conditions / Privacy Policy");
        setVersion(p.version || "1.0");
      }
    } catch {
      message.error("Failed to load policy");
    } finally {
      setLoading(false);
    }
  };

  const fetchAcceptances = async () => {
    setAccLoading(true);
    try {
      const res = await policyAPI.getAcceptances({ search });
      setAcceptances(res?.data?.users || []);
    } catch {
      message.error("Failed to load acceptances");
    } finally {
      setAccLoading(false);
    }
  };

  useEffect(() => { fetchPolicy(); }, []);
  useEffect(() => { fetchAcceptances(); }, []);

  const handleSave = async () => {
    if (!audiences.length) { message.warning("Select patients, doctors, or both"); return; }
    if (!content.trim()) { message.warning("Policy content cannot be empty"); return; }
    setSaving(true);
    try {
      await policyAPI.upsert({ content, title, version, audiences });
      message.success("Policy saved successfully");
    } catch {
      message.error("Failed to save policy");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <Text strong>{v}</Text> },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Role", dataIndex: "role", key: "role", render: value => value === "doctor" ? "Doctor" : "Patient" },
    { title: "Phone", dataIndex: "phone", key: "phone" },
    {
      title: "E-Signature",
      dataIndex: "eSignature",
      key: "eSignature",
      render: (v) => <Text italic style={{ color: "#0A6EBD" }}>{v || "—"}</Text>,
    },
    {
      title: "Policy Version",
      dataIndex: "policyVersion",
      key: "policyVersion",
      render: (v) => <Tag color="blue">{v || "1.0"}</Tag>,
    },
    {
      title: "Accepted At",
      dataIndex: "policyAcceptedAt",
      key: "policyAcceptedAt",
      render: (v) => v ? dayjs(v).format("DD MMM YYYY, hh:mm A") : "—",
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            <FileProtectOutlined style={{ marginRight: 8 }} />
            Policy Management
          </Title>
          <Text type="secondary">Manage app Terms & Conditions / Privacy Policy</Text>
        </div>
      </div>

      <Tabs defaultActiveKey="editor" items={[
        {
          key: "editor",
          label: "Edit Policy",
          children: (
            <Card loading={loading}>
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} md={16}>
                  <Text strong>Policy Title</Text>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    style={{ marginTop: 4 }}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <Text strong>Version</Text>
                  <Input
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder="e.g. 1.0"
                    style={{ marginTop: 4 }}
                  />
                </Col>
              </Row>
              <div>
                <Text strong id="policy-audience-label">Applies to</Text>
                <div style={{ marginTop: 8 }}>
                  <Space wrap role="group" aria-labelledby="policy-audience-label">
                    {['patient', 'doctor'].map(role => <Checkbox key={role} checked={audiences.includes(role)} onChange={e => setAudiences(e.target.checked ? [...audiences, role] : audiences.filter(value => value !== role))}>{role === 'patient' ? 'Patients' : 'Doctors'}</Checkbox>)}
                  </Space>
                </div>
              </div>
              <Divider />
              <Text strong>Policy Content</Text>
              <TextArea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={20}
                placeholder="Enter your full Terms & Conditions and Privacy Policy text here..."
                style={{ marginTop: 8, fontFamily: "monospace", fontSize: 13 }}
              />
              <div style={{ marginTop: 16, textAlign: "right" }}>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  size="large"
                  loading={saving}
                  onClick={handleSave}
                >
                  Save Policy
                </Button>
              </div>
            </Card>
          ),
        },
        {
          key: "acceptances",
          label: `Acceptances`,
          children: (
            <Card>
              <Space style={{ marginBottom: 16 }}>
                <Input.Search
                  placeholder="Search by name or email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onSearch={fetchAcceptances}
                  allowClear
                  style={{ width: 280 }}
                />
                <Button icon={<ReloadOutlined />} onClick={fetchAcceptances}>Refresh</Button>
              </Space>
              <Table
                columns={columns}
                dataSource={acceptances}
                rowKey="_id"
                loading={accLoading}
                scroll={{ x: "max-content" }}
              />
            </Card>
          ),
        },
      ]} />
    </div>
  );
};

export default Policy;
